/**
 * CreateReviewModal Component
 *
 * Universal modal for leaving reviews after order completion
 * Supports both contractor and client ratings:
 * - Client → Worker: quality, professionalism, communication, value
 * - Worker → Client: communication, professionalism, payment
 */

'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import { reviewsApi, type ContractorRatings, type ClientRatings } from '@/src/shared/api/reviews.api';
import { cn } from '@/src/shared/lib/utils';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Star } from 'lucide-react';

interface CreateReviewModalProps extends BaseModalProps {
  orderId?: string;
  orderTitle?: string;
  reviewerRole?: 'client' | 'worker'; // Who is leaving the review
  revieweeName?: string; // Name of person being reviewed (worker or client)
  revieweeAvatarUrl?: string;
  // Edit mode
  reviewId?: string; // If provided, modal is in edit mode
  initialData?: {
    rating: number;
    comment: string;
    criteriaRatings: ContractorRatings | ClientRatings;
  };
  // Deprecated props (for backward compatibility)
  workerName?: string;
  workerAvatarUrl?: string;
  onSuccess?: () => void;
}

const CONTRACTOR_RATING_CRITERIA = [
  { key: 'quality', labelKey: 'quality' },
  { key: 'professionalism', labelKey: 'professionalism' },
  { key: 'communication', labelKey: 'communication' },
  { key: 'value', labelKey: 'value' },
] as const;

const CLIENT_RATING_CRITERIA = [
  { key: 'communication', labelKey: 'communication' },
  { key: 'professionalism', labelKey: 'professionalism' },
  { key: 'payment', labelKey: 'payment' },
] as const;

/**
 * Create Review Modal - universal for both clients and workers
 * Usage:
 * - Client: open({ orderId: '123', reviewerRole: 'client', revieweeName: 'John' })
 * - Worker: open({ orderId: '123', reviewerRole: 'worker', revieweeName: 'Jane' })
 */
export function CreateReviewModal({
  isOpen,
  onClose,
  orderId,
  orderTitle,
  reviewerRole = 'client', // Default to client for backward compatibility
  revieweeName,
  revieweeAvatarUrl,
  // Edit mode
  reviewId,
  initialData,
  // Deprecated props
  workerName,
  workerAvatarUrl,
  onSuccess,
}: CreateReviewModalProps) {
  const t = useTranslations('reviews');
  const queryClient = useQueryClient();

  // Use new props or fall back to deprecated ones
  const targetName = revieweeName || workerName;
  const targetAvatarUrl = revieweeAvatarUrl || workerAvatarUrl;
  const isReviewingContractor = reviewerRole === 'client';
  const isEditMode = !!reviewId;

  // Initialize ratings based on reviewer role or initial data (edit mode)
  const [ratings, setRatings] = useState<ContractorRatings | ClientRatings>(
    initialData?.criteriaRatings ||
    (isReviewingContractor
      ? { quality: 0, professionalism: 0, communication: 0, value: 0 }
      : { communication: 0, professionalism: 0, payment: 0 })
  );

  const [hoveredRatings, setHoveredRatings] = useState<Partial<ContractorRatings | ClientRatings>>({});
  const [comment, setComment] = useState(initialData?.comment || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRatingChange = (criterion: string, value: number) => {
    // Ensure integer values (backend validation requires integers)
    setRatings((prev) => ({ ...prev, [criterion]: Math.round(value) }));
  };

  const handleRatingHover = (criterion: string, value: number | null) => {
    if (value === null) {
      setHoveredRatings((prev) => {
        const newHovered = { ...prev };
        delete newHovered[criterion];
        return newHovered;
      });
    } else {
      setHoveredRatings((prev) => ({ ...prev, [criterion]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orderId) {
      toast.error(t('errors.missingOrderId'));
      return;
    }

    // Check if all ratings are provided
    const allRated = Object.values(ratings).every((r) => r > 0);
    if (!allRated) {
      toast.error(t('errors.allCriteriaRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare review data based on reviewer role
      let criteriaRatings: ContractorRatings | ClientRatings;

      if (isReviewingContractor) {
        // Client reviewing worker (contractor)
        criteriaRatings = {
          quality: Math.round((ratings as ContractorRatings).quality),
          professionalism: Math.round((ratings as ContractorRatings).professionalism),
          communication: Math.round((ratings as ContractorRatings).communication),
          value: Math.round((ratings as ContractorRatings).value),
        };
      } else {
        // Worker reviewing client
        criteriaRatings = {
          communication: Math.round((ratings as ClientRatings).communication),
          professionalism: Math.round((ratings as ClientRatings).professionalism),
          payment: Math.round((ratings as ClientRatings).payment),
        };
      }

      // Validate all ratings are between 1-5
      const invalidRatings = Object.entries(criteriaRatings).filter(
        ([_, value]) => value < 1 || value > 5
      );
      if (invalidRatings.length > 0) {
        console.error('Invalid ratings:', invalidRatings);
        toast.error('All ratings must be between 1 and 5');
        return;
      }

      const reviewData = {
        orderId,
        criteriaRatings,
        ...(comment.trim() && { comment: comment.trim() }),
      };

      console.log(isEditMode ? '=== UPDATING REVIEW ===' : '=== SUBMITTING REVIEW ===');
      console.log('Review data:', JSON.stringify(reviewData, null, 2));
      console.log('Criteria ratings:', criteriaRatings);
      console.log('Order ID:', orderId);
      console.log('Comment:', comment.trim() || '(none)');
      if (isEditMode) {
        console.log('Review ID:', reviewId);
      }

      if (isEditMode && reviewId) {
        // Update existing review
        await reviewsApi.update(reviewId, reviewData);
      } else {
        // Create new review
        await reviewsApi.create(reviewData);
      }

      // Invalidate reviews queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['profile-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['worker-orders'] });
      queryClient.invalidateQueries({ queryKey: ['client-orders'] });
      queryClient.invalidateQueries({ queryKey: ['order-reviews', orderId] });

      toast.success(isEditMode ? t('success.updated') : t('success.created'));
      onClose();
      setRatings({ quality: 0, professionalism: 0, communication: 0, value: 0 });
      setComment('');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to create review:', error);

      // Extract error message and map to translated keys
      let errorMessage = t('errors.createFailed');

      if (error instanceof Error) {
        const msg = error.message.toLowerCase();

        // Map backend error messages to translated keys
        if (msg.includes('already reviewed')) {
          errorMessage = t('errors.alreadyReviewed');
        } else if (msg.includes('deadline')) {
          errorMessage = t('errors.deadlinePassed');
        } else if (msg.includes('completed')) {
          errorMessage = t('errors.orderNotCompleted');
        } else {
          errorMessage = error.message || t('errors.createFailed');
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast.error(errorMessage);

      // Close modal if already reviewed (no point keeping it open)
      if (errorMessage === t('errors.alreadyReviewed')) {
        setTimeout(() => onClose(), 2000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      // Reset form based on reviewer role
      setRatings(
        isReviewingContractor
          ? { quality: 0, professionalism: 0, communication: 0, value: 0 }
          : { communication: 0, professionalism: 0, payment: 0 }
      );
      setComment('');
      setHoveredRatings({});
    }
  };

  const averageRating =
    Object.values(ratings).reduce((sum, r) => sum + r, 0) / Object.values(ratings).length || 0;

  const allRated = Object.values(ratings).every((r) => r > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      animation="zoom"
      title={isEditMode ? t('editModal.title') : t('createModal.title')}
      showCloseButton={!isSubmitting}
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Reviewee info */}
        {targetName && (
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            {targetAvatarUrl && targetAvatarUrl !== 'undefined' ? (
              <img
                src={targetAvatarUrl}
                alt={targetName}
                className="w-12 h-12 rounded-full object-cover"
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : null}
            {(!targetAvatarUrl || targetAvatarUrl === 'undefined') && (
              <div className="w-12 h-12 rounded-full bg-accent-primary text-white flex items-center justify-center text-lg font-semibold">
                {targetName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <Typography variant="body" weight="semibold" color="primary">
                {targetName}
              </Typography>
              {orderTitle && (
                <Typography variant="bodySm" color="secondary">
                  {orderTitle}
                </Typography>
              )}
            </div>
          </div>
        )}

        {/* Rating criteria */}
        <div className="space-y-4">
          <Typography variant="body" weight="medium" color="primary">
            {t('createModal.rateExperience')}
          </Typography>

          {(isReviewingContractor ? CONTRACTOR_RATING_CRITERIA : CLIENT_RATING_CRITERIA).map(({ key, labelKey }) => (
            <div key={key} className="space-y-2">
              <div className="flex items-center justify-between">
                <Typography variant="bodySm" color="secondary">
                  {t(`criteria.${labelKey}`)}
                </Typography>
                {(ratings as any)[key] > 0 && (
                  <Typography variant="bodySm" weight="medium" color="primary">
                    {(ratings as any)[key]}/5
                  </Typography>
                )}
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((value) => {
                  const isFilled =
                    value <= ((hoveredRatings as any)[key] || (ratings as any)[key] || 0);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleRatingChange(key, value)}
                      onMouseEnter={() => handleRatingHover(key, value)}
                      onMouseLeave={() => handleRatingHover(key, null)}
                      className="focus:outline-none transition-transform hover:scale-110"
                      disabled={isSubmitting}
                    >
                      <Star
                        className={cn(
                          'w-8 h-8 transition-colors',
                          isFilled
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        )}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Overall rating display */}
        {allRated && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <Typography variant="bodySm" weight="medium" color="primary" className="text-green-900">
                {t('createModal.overallRating')}
              </Typography>
              <div className="flex items-center gap-2">
                <Typography variant="h3" weight="bold" color="primary" className="text-green-900">
                  {averageRating.toFixed(1)}
                </Typography>
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              </div>
            </div>
          </div>
        )}

        {/* Comment */}
        <div>
          <label htmlFor="comment" className="block mb-2">
            <Typography variant="bodySm" weight="medium" color="secondary">
              {t('createModal.comment')} <span className="text-gray-400">({t('createModal.optional')})</span>
            </Typography>
          </label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            maxLength={1000}
            className="w-full px-3 py-2 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary resize-none"
            placeholder={t('createModal.commentPlaceholder')}
            disabled={isSubmitting}
          />
          <Typography variant="bodySm" color="tertiary" className="mt-1">
            {comment.length}/1000
          </Typography>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-3 border-2 border-border-primary text-text-primary rounded-lg hover:bg-gray-50 transition-colors font-medium"
            disabled={isSubmitting}
          >
            {t('createModal.cancel')}
          </button>
          <button
            type="submit"
            disabled={!allRated || isSubmitting}
            className={cn(
              'flex-1 px-4 py-3 rounded-lg font-medium transition-colors',
              'bg-[#6AB344] text-white hover:bg-[#5A9B38]',
              'disabled:bg-gray-300 disabled:cursor-not-allowed'
            )}
          >
            {isSubmitting
              ? (isEditMode ? t('editModal.updating') : t('createModal.submitting'))
              : (isEditMode ? t('editModal.update') : t('createModal.submit'))}
          </button>
        </div>
      </form>
    </Modal>
  );
}
